import { Grid, makeStyles, Typography } from '@material-ui/core/';
import moment from 'moment';
import React, { useState, useEffect } from 'react';
import {
  FormDataConsumer,
  NumberInput,
  SelectInput,
  TextInput
} from 'react-admin';
import { Field } from 'react-final-form';

import CheckBoxField from '../../core/components/CheckBoxField';
import RichTextInput from '../../core/components/inputs/RichTextInput';
import TimeInput from '../../core/components/inputs/TimeInput';
import { TimeZones } from './TenantConfigurationLookups';
import Checkbox from '@material-ui/core/Checkbox';
import InputLabel from '@material-ui/core/InputLabel';

const useStyles = makeStyles({
  headerContainer: {
    paddingTop: '0px !important'
  },
  formCheckBox: {
    marginTop: '19px'
  },
  checkboxContainer: {
    paddingRight: '8px'
  },
  checkboxLabel: {
    fontSize: '14px',
    lineHeight: '20px',
    whiteSpace: 'nowrap'
  },
  checkbox: {
    padding: 0,
    margin: '7px 0'
  },
  gridContainer: {
    display: 'flex',
    alignItems: 'center'
  }
});

const CheckboxShow = props => {
  const { label } = props;
  const classes = useStyles();
  return (
    <div className={classes.checkboxContainer}>
      <InputLabel htmlFor={label} className={classes.checkboxLabel}>
        {label}
      </InputLabel>
      <Checkbox
        {...props}
        className={classes.checkbox}
        disabled={props.disabled}
      />
    </div>
  );
};

/**
 * Hours Interval Input: expected value from '00' to '23'
 * uncheck the Checkbox will set the value to ''
 * check the Checkbox will set the value to '00'
 * @param {*} props
 */
const IntervalInput = props => {
  const {
    onChange,
    onCheckboxChange,
    multipleSource,
    source,
    variant,
    label,
    disabled,
    record,
    formData
  } = props;
  const currentValue = multipleSource ? formData[source] : record[source];
  const [checked, setChecked] = useState(!!currentValue);

  useEffect(() => {
    setChecked(!!currentValue);
  }, [currentValue]);

  const hoursParser = value => {
    if (value) {
      const val = parseInt(value, 10);
      if (val < 10) {
        return '0' + val;
      }
      return value;
    }
    return '';
  };

  const handleCheckboxChange = () => {
    onCheckboxChange(checked, source, multipleSource, formData);
    setChecked(!checked);
  };

  return (
    <React.Fragment>
      <CheckboxShow
        label={label}
        checked={checked}
        onChange={handleCheckboxChange}
        disabled={disabled}
      />
      <NumberInput
        helperText={false}
        source={source}
        label=""
        variant={variant}
        parse={hoursParser}
        format={hoursParser}
        disabled={disabled ? disabled : !checked}
        onChange={onChange}
        inputProps={{
          min: 0,
          max: 23
        }}
      />
    </React.Fragment>
  );
};

/**
 * Time & Time Zone Input: expected value 'HH:mm +HH:mm' or 'HH:mm -HH:mm'
 * expected value for Time component a string in time fromat 'HH:mm'
 * expected value for Time Zone component a string format '+HH:mm' or '-HH:mm'
 * uncheck the Checkbox will set the Time and the Time Zone values to ''
 * check the Checkbox will set the Time value to '00:00' and the Time Zone value to '+00:00'
 * @param {*} props
 */
const TimeZoneInput = props => {
  const {
    onTimeChange,
    onZoneChange,
    onCheckboxChange,
    source,
    variant,
    label,
    formData
  } = props;
  const classes = useStyles();
  const currentValue = formData[source];
  const [checked, setChecked] = useState(!!currentValue);

  useEffect(() => {
    setChecked(!!currentValue);
  }, [currentValue]);

  const handleCheckboxChange = () => {
    onCheckboxChange(checked, formData);
    setChecked(!checked);
  };

  return (
    <React.Fragment>
      <Grid item xs={12} md={6} lg={4} className={classes.gridContainer}>
        <CheckboxShow
          label={label}
          checked={checked}
          disabled={!formData.editable}
          onChange={handleCheckboxChange}
        />
        <TimeInput
          source="time"
          label=""
          variant={variant}
          disabled={!formData.editable ? true : !checked}
          onChange={value => {
            onTimeChange(value, formData);
          }}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <SelectInput
          source="zone"
          label=" "
          choices={TimeZones}
          variant={variant}
          disabled={!formData.editable ? true : !checked}
          options={{ autoComplete: 'off' }}
          helperText={false}
          onChange={event => {
            onZoneChange(event.target.value, formData);
          }}
        />
      </Grid>
    </React.Fragment>
  );
};

const ValueInput = props => {
  const { variant, classes, record = {} } = props;
  const { type } = record;

  const handleIntervalCheckboxChange = (
    checked,
    source,
    multipleSource,
    formData
  ) => {
    const { formApi } = props;
    formApi.change(source, checked ? '' : '00');
    if (multipleSource) {
      const { value = '' } = formData;
      const parts = value.split('#');
      const part1 = parts[0] || '';
      const part2 = parts[1] || '';

      let newValue;
      if (source === 'interval0') {
        formApi.change('interval1', '');
        formApi.change('interval2', '');
        newValue = checked ? '' : '00##';
      } else if (source === 'interval1') {
        formApi.change('interval2', '');
        newValue = checked ? `${part1}##` : `${part1}#00#`;
      } else {
        newValue = checked ? `${part1}#${part2}#` : `${part1}#${part2}#00`;
      }
      formApi.change(multipleSource, newValue);
    }
  };

  /**
   * handle interval change: if multiple intervals set 'value' to :
   * one interval active 'val1##'
   * two intervals active 'val1#val2#'
   * tree intervals active 'val1#val2#val3'
   * @param {*} interval
   * @param {*} formData
   * @param {*} source
   */
  const handleIntervalChange = (interval, formData, source) => {
    const { formApi } = props;
    const { value = '' } = formData;
    const parts = value.split('#');
    const part1 = parts[0] || '';
    const part2 = parts[1] || '';
    const part3 = parts[2] || '';

    if (interval) {
      const val = parseInt(interval, 10);
      if (val < 10) {
        interval = '0' + val;
      }
    } else {
      interval = '';
    }

    let newValue;
    if (source === 'interval0') {
      newValue = `${interval}#${part2}#${part3}`;
    } else if (source === 'interval1') {
      newValue = `${part1}#${interval}#${part3}`;
    } else {
      newValue = `${part1}#${part2}#${interval}`;
    }

    formApi.change('value', newValue);
  };

  const handleTimeZoneCheckboxChange = checked => {
    const { formApi } = props;
    formApi.change('value', checked ? '' : '00:00 +00:00');
    formApi.change('time', checked ? '' : '00:00');
    formApi.change('zone', checked ? '' : '+00:00');
  };

  const handleTimeChange = (timeValue, formData) => {
    const { formApi } = props;
    const time = timeValue && moment(timeValue).format('HH:mm');
    const newValue = time + ' ' + formData.zone;

    formApi.change('value', newValue);
  };

  const handleZoneChange = (zoneValue, formData) => {
    const { formApi } = props;
    const newValue = formData.time + ' ' + zoneValue;

    formApi.change('value', newValue);
  };

  const booleanParser = value => {
    if (value) {
      return '1';
    }
    return '0';
  };

  const textParser = value => {
    if (!value) {
      return '';
    }
    return value;
  };

  switch (type) {
    case 'CHECKBOX_O':
      return (
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData && (
              <Grid item xs={12} className={classes.formCheckBox}>
                <Field
                  name="value"
                  label={
                    formData.value === 'false' ||
                    formData.value === '0' ||
                    !formData.value
                      ? 'OFF'
                      : 'ON'
                  }
                  disabled={!formData.editable}
                  variant={variant}
                  component={CheckBoxField}
                  isChecked={value =>
                    !(value === 'false' || formData.value === '0' || !value)
                  }
                  parse={booleanParser}
                  type="checkbox"
                  id="Checkbox"
                />
              </Grid>
            )
          }
        </FormDataConsumer>
      );
    case 'TIME_I':
      return (
        <FormDataConsumer>
          {({ formData }) =>
            formData && (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                className={classes.gridContainer}
              >
                <IntervalInput
                  source="value"
                  label="Value"
                  disabled={!formData.editable}
                  variant={variant}
                  onCheckboxChange={handleIntervalCheckboxChange}
                  record={record}
                  id="Value"
                />
              </Grid>
            )
          }
        </FormDataConsumer>
      );
    case 'TIME_M':
      return (
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData && (
              <React.Fragment>
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  className={classes.gridContainer}
                >
                  <IntervalInput
                    multipleSource="value"
                    source="interval0"
                    label="Interval1"
                    variant={variant}
                    disabled={!formData.editable}
                    onCheckboxChange={handleIntervalCheckboxChange}
                    record={record}
                    formData={formData}
                    id="Interval1"
                    onChange={event =>
                      handleIntervalChange(
                        event.target.value,
                        formData,
                        'interval0'
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  className={classes.gridContainer}
                >
                  <IntervalInput
                    multipleSource="value"
                    source="interval1"
                    label="Interval2"
                    variant={variant}
                    onCheckboxChange={handleIntervalCheckboxChange}
                    record={record}
                    formData={formData}
                    disabled={!formData.interval0}
                    id="Interval2"
                    onChange={event =>
                      handleIntervalChange(
                        event.target.value,
                        formData,
                        'interval1'
                      )
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  className={classes.gridContainer}
                >
                  <IntervalInput
                    multipleSource="value"
                    source="interval2"
                    label="Interval3"
                    variant={variant}
                    onCheckboxChange={handleIntervalCheckboxChange}
                    record={record}
                    formData={formData}
                    disabled={formData.interval1}
                    id="Interval3"
                    onChange={event =>
                      handleIntervalChange(
                        event.target.value,
                        formData,
                        'interval2'
                      )
                    }
                  />
                </Grid>
              </React.Fragment>
            )
          }
        </FormDataConsumer>
      );
    case 'TIME_T':
      return (
        <FormDataConsumer>
          {({ formData }) =>
            formData && (
              <TimeZoneInput
                label="Time Zone"
                source="value"
                variant={variant}
                formData={formData}
                onCheckboxChange={handleTimeZoneCheckboxChange}
                onTimeChange={handleTimeChange}
                onZoneChange={handleZoneChange}
                id="Time Zone"
              />
            )
          }
        </FormDataConsumer>
      );
    case 'EDITOR_C':
      return (
        <FormDataConsumer>
          {({ formData }) =>
            formData && (
              <Grid>
                <RichTextInput
                  source="value"
                  label="Value"
                  parse={textParser}
                  disabled={!formData.editable}
                  displayingVariables={false}
                  editorConfig={{ height: 400 }}
                  id="Value"
                />
              </Grid>
            )
          }
        </FormDataConsumer>
      );
    default:
      return (
        <FormDataConsumer>
          {({ formData }) =>
            formData && (
              <Grid>
                <TextInput
                  multiline
                  source="value"
                  label="Value"
                  rows="4"
                  disabled={!formData.editable}
                  parse={textParser}
                  options={{ autoComplete: 'off' }}
                  variant={variant}
                  id="Value"
                />
              </Grid>
            )
          }
        </FormDataConsumer>
      );
  }
};

const TenantConfigurationSummary = props => {
  const classes = useStyles();

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={12} className={classes.headerContainer}>
          <Typography variant="h5">Global Configuration Details</Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInput
                source="name"
                label="Name"
                initialValue=""
                options={{ autoComplete: 'off' }}
                variant={props.variant}
                disabled
                id="Name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="category"
                label="Category"
                initialValue=""
                options={{ autoComplete: 'off' }}
                variant={props.variant}
                disabled
                id="Category"
              />
            </Grid>
            <Grid item xs={12}>
              <ValueInput {...props} classes={classes} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={4}>
          <TextInput
            multiline
            source="description"
            label="Description"
            rows="15"
            initialValue=""
            options={{ autoComplete: 'off' }}
            variant={props.variant}
            disabled
            id="Description"
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default TenantConfigurationSummary;
